import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { authApi } from "../api/auth";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { applyPreferences, initialPreferences, readEnv, sanitizePreferences, storePreferences, type ThemeEnv } from "./applyPreferences";
import { DEFAULT_PREFERENCES, THEMES, effectiveTheme, type ThemeId, type UiPreferences } from "./themes";

type ThemeContextValue = {
  preferences: UiPreferences;
  theme: ThemeId;
  setPreferences: (partial: Partial<UiPreferences>) => Promise<void>;
  toggleDarkLight: () => Promise<void>;
  reset: () => Promise<void>;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const TRANSITION_MS = 300;

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const toast = useToast();
  const { t } = useTranslation();
  const [env, setEnv] = useState<ThemeEnv>(readEnv);
  const [preferences, setLocal] = useState<UiPreferences>(initialPreferences);
  const transitionTimer = useRef<number | undefined>(undefined);
  // The preferences on screen, readable by a save that starts after later clicks.
  const localRef = useRef<UiPreferences>(preferences);
  // Last value the server confirmed — what a failed save rolls back to.
  const confirmedRef = useRef<UiPreferences>(preferences);
  // Saves run one at a time: each starts after the previous one has settled and
  // sends the full object on screen at that moment, so requests never overlap
  // and an older one can never land on top of a newer one.
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  // Numbers every setPreferences call. A queued save that is no longer the latest
  // is skipped (the latest sends the same full object), and only the latest
  // save's outcome changes what is on screen.
  const seqRef = useRef(0);
  // Calls numbered up to here were made before the signed-in user last changed.
  const userStartRef = useRef(0);

  const userId = state.status === "authenticated" ? state.user.id : null;

  const show = useCallback((p: UiPreferences) => {
    localRef.current = p;
    setLocal(p);
  }, []);

  // The signed-in user's saved choice wins over whatever this browser last showed —
  // but only when the signed-in user actually changes. Keyed on the id (not on
  // `state` itself) so a plain refresh() for the same user (e.g. after editing the
  // profile) does not overwrite a preference the user just changed locally; the
  // saved object never round-trips back through AuthContext. Sanitised so a
  // partial or unexpected server value cannot produce an undefined attribute
  // on <html>.
  useEffect(() => {
    // Saves still queued for the previous user are dropped, and responses to the
    // ones already sent no longer change what the new user sees.
    userStartRef.current = ++seqRef.current;
    if (state.status === "authenticated") {
      const server = sanitizePreferences(state.user.uiPreferences) ?? DEFAULT_PREFERENCES;
      confirmedRef.current = server;
      show(server);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the user id should retrigger this; state.user.uiPreferences is read fresh when it fires.
  }, [userId]);

  useEffect(() => {
    if (!window.matchMedia) return;
    const dark = window.matchMedia("(prefers-color-scheme: dark)");
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setEnv({ systemDark: dark.matches, systemReducedMotion: motion.matches });
    dark.addEventListener("change", update);
    motion.addEventListener("change", update);
    return () => { dark.removeEventListener("change", update); motion.removeEventListener("change", update); };
  }, []);

  useEffect(() => {
    applyPreferences(preferences, env);
    storePreferences(preferences);
  }, [preferences, env]);

  const withTransition = useCallback(() => {
    const root = document.documentElement;
    root.classList.add("theme-transition");
    window.clearTimeout(transitionTimer.current);
    transitionTimer.current = window.setTimeout(() => root.classList.remove("theme-transition"), TRANSITION_MS);
  }, []);

  const setPreferences = useCallback((partial: Partial<UiPreferences>): Promise<void> => {
    const mine = ++seqRef.current;
    withTransition();
    show({ ...localRef.current, ...partial });
    if (state.status !== "authenticated") return Promise.resolve();

    const previousUser = () => mine <= userStartRef.current;
    const latest = () => mine === seqRef.current;
    const save = async () => {
      if (previousUser() || !latest()) return;
      try {
        const saved = await authApi.updatePreferences(localRef.current);
        if (previousUser()) return;
        confirmedRef.current = saved;
        // A newer call is queued behind this one; its own save reconciles the screen.
        if (latest()) show(saved);
      } catch {
        if (previousUser()) return;
        toast.error(t("appearance.saveError"));
        if (latest()) show(confirmedRef.current);
      }
    };
    const run = saveChain.current.then(save);
    saveChain.current = run;
    return run;
  }, [state.status, toast, t, withTransition, show]);

  const toggleDarkLight = useCallback(async () => {
    const current = effectiveTheme(preferences, env.systemDark);
    await setPreferences({ followSystem: false, theme: THEMES[current].pair });
  }, [preferences, env.systemDark, setPreferences]);

  const reset = useCallback(() => setPreferences(DEFAULT_PREFERENCES), [setPreferences]);

  const value = useMemo<ThemeContextValue>(() => ({
    preferences,
    theme: effectiveTheme(preferences, env.systemDark),
    setPreferences,
    toggleDarkLight,
    reset,
  }), [preferences, env.systemDark, setPreferences, toggleDarkLight, reset]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
